/**
 * Bank Account Field Mapping
 */

import { FieldMap } from './types';

/**
 * Bank Account Field Map
 * 
 * UI Field           | Parse V1              | Parse V2
 * -------------------|----------------------|------------------------
 * id                 | objectId             | objectId
 * bankCode           | bank_code            | bank.code
 * bankName           | bank_name            | bank.name
 * bankLogo           | bank_logo            | bank.logo
 * accountNumber      | account_number       | accountNumber
 * accountName        | account_name         | accountName
 * type               | type                 | accountType
 * status             | status               | status
 * balance            | balance              | balance
 * dailyLimit         | daily_limit          | limits.daily
 * dailyUsed          | daily_used           | limits.dailyUsed
 * minAmount          | min_amount           | limits.min
 * maxAmount          | max_amount           | limits.max
 * isAutoTransfer     | is_auto_transfer     | settings.autoTransfer
 * priority           | priority             | priority
 * lastTransaction    | last_transaction     | lastTransactionAt
 * createdAt          | createdAt            | createdAt
 * updatedAt          | updatedAt            | updatedAt
 */
export const bankAccountFieldMap: FieldMap = {
  id: {
    uiField: 'id',
    parseV1Field: 'objectId',
    parseV2Field: 'objectId',
    required: true,
  },
  bankCode: {
    uiField: 'bankCode',
    parseV1Field: 'bank_code',
    parseV2Field: 'bank.code',
    required: true,
  },
  bankName: {
    uiField: 'bankName',
    parseV1Field: 'bank_name',
    parseV2Field: 'bank.name',
    required: true,
  },
  bankLogo: {
    uiField: 'bankLogo',
    parseV1Field: 'bank_logo',
    parseV2Field: 'bank.logo',
  },
  accountNumber: {
    uiField: 'accountNumber',
    parseV1Field: 'account_number',
    parseV2Field: 'accountNumber',
    required: true,
  },
  accountName: {
    uiField: 'accountName',
    parseV1Field: 'account_name',
    parseV2Field: 'accountName',
    required: true,
  },
  type: {
    uiField: 'type',
    parseV1Field: 'type',
    parseV2Field: 'accountType',
    required: true,
    fromParse: (value, version) => {
      if (version === 'v1') {
        const typeMap: Record<string, string> = {
          '1': 'deposit',
          '2': 'withdraw',
          '3': 'both',
        };
        return typeMap[String(value)] || value;
      }
      return value;
    },
  },
  status: {
    uiField: 'status',
    parseV1Field: 'status',
    parseV2Field: 'status',
    defaultValue: 'active',
    fromParse: (value, version) => {
      if (version === 'v1') {
        const statusMap: Record<string, string> = {
          '1': 'active',
          '0': 'inactive',
          '2': 'maintenance',
        };
        return statusMap[String(value)] || value;
      }
      return value;
    },
  },
  balance: {
    uiField: 'balance',
    parseV1Field: 'balance',
    parseV2Field: 'balance',
    defaultValue: 0,
    fromParse: (value) => Number(value) || 0,
  },
  dailyLimit: {
    uiField: 'dailyLimit',
    parseV1Field: 'daily_limit',
    parseV2Field: 'limits.daily',
    defaultValue: 0,
    fromParse: (value) => Number(value) || 0,
  },
  dailyUsed: {
    uiField: 'dailyUsed',
    parseV1Field: 'daily_used',
    parseV2Field: 'limits.dailyUsed',
    defaultValue: 0,
    fromParse: (value) => Number(value) || 0,
  },
  minAmount: {
    uiField: 'minAmount',
    parseV1Field: 'min_amount',
    parseV2Field: 'limits.min',
    defaultValue: 0,
    fromParse: (value) => Number(value) || 0,
  },
  maxAmount: {
    uiField: 'maxAmount',
    parseV1Field: 'max_amount',
    parseV2Field: 'limits.max',
    defaultValue: 0,
    fromParse: (value) => Number(value) || 0,
  },
  isAutoTransfer: {
    uiField: 'isAutoTransfer',
    parseV1Field: 'is_auto_transfer',
    parseV2Field: 'settings.autoTransfer',
    defaultValue: false,
    fromParse: (value) => Boolean(value),
  },
  priority: {
    uiField: 'priority',
    parseV1Field: 'priority',
    parseV2Field: 'priority',
    defaultValue: 0,
    fromParse: (value) => Number(value) || 0,
  },
  lastTransaction: {
    uiField: 'lastTransaction',
    parseV1Field: 'last_transaction',
    parseV2Field: 'lastTransactionAt',
    fromParse: (value) => value ? new Date(value as string).toISOString() : undefined,
  },
  createdAt: {
    uiField: 'createdAt',
    parseV1Field: 'createdAt',
    parseV2Field: 'createdAt',
    fromParse: (value) => value ? new Date(value as string).toISOString() : undefined,
  },
  updatedAt: {
    uiField: 'updatedAt',
    parseV1Field: 'updatedAt',
    parseV2Field: 'updatedAt',
    fromParse: (value) => value ? new Date(value as string).toISOString() : undefined,
  },
};

/**
 * Parse Class Name
 */
export const BANK_ACCOUNT_CLASS_NAME = {
  v1: 'BankAccount',
  v2: 'BankAccount',
} as const;













